import { Fragment, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, RotateCcw } from "lucide-react";
import { Splash } from "../components/chat/Splash";
import { ChatHeader } from "../components/chat/ChatHeader";
import { Bubble, Typing } from "../components/chat/Bubble";
import { ShortsVideo } from "../components/chat/ShortsVideo";
import { PriceCard } from "../components/chat/PriceCard";
import { Choices } from "../components/chat/Choices";
import { Composer } from "../components/chat/Composer";
import { LiveContent } from "../components/chat/LiveContent";
import { PushBanner } from "../components/chat/PushBanner";
import { firstVisitorIndex } from "../lib/hours";
import { OpenChromeBar } from "../components/chat/OpenChromeBar";
import { useLiveChat } from "../hooks/useLiveChat";
import { isValidPhone, msgTime, phoneDigits } from "../lib/live";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { InstallGuide as InstallHelp } from "../components/support/InstallGuide";
import { useInstall, reportInstalled } from "../lib/install";
import { buildScript, logAction, nowTime, waLink } from "../lib/chat";
import { useBrand } from "../context/brand";

const farewell = (b) => b.farewell || `Tudo bem, ${b.fan}! Obrigada pela visita. Se mudar de ideia, é só voltar aqui. Um abraço da equipe ${b.name}!`;
const COMPOSE = ["ask-name", "ask-phone", "starting", "live"];

const Content = ({ m, onInstall }) => {
  if (m.type === "video") return <ShortsVideo />;
  if (m.type === "price") return <PriceCard />;
  if (m.type === "install") return (
    <>
      <p className="lz-text">{m.text}</p>
      <button data-testid="chat-install-early-button" className="lz-install-btn" onClick={onInstall}>
        <Download size={17} /> Instalar app de suporte
      </button>
    </>
  );
  return <p className="lz-text">{m.text}</p>;
};

export default function ChatPage() {
  const [splash, setSplash] = useState(true);
  const [msgs, setMsgs] = useState([]);
  const [typing, setTyping] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [phase, setPhase] = useState("script");
  const [origem, setOrigem] = useState(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const live = useLiveChat();
  const inst = useInstall();
  const brand = useBrand();
  const endRef = useRef(null);

  const push = (m) => setMsgs((prev) => [...prev, { ...m, id: prev.length, time: nowTime() }]);

  useEffect(() => {
    const t = setTimeout(() => setSplash(false), 2100);
    logAction("view");
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (splash) return undefined;
    if (live.chat?.hasSession()) {
      live.chat.resume();
      reportInstalled();
      setPhase("live");
      return undefined;
    }
    const timers = [];
    let at = 400;
    (brand.script || buildScript(brand.name, brand.fan, brand.intro)).forEach((m) => {
      const typeFor = m.type === "text" ? 700 + m.text.length * 7 : 900;
      timers.push(setTimeout(() => setTyping(true), at));
      at += typeFor;
      timers.push(setTimeout(() => { setTyping(false); push(m); }, at));
      at += 350;
    });
    timers.push(setTimeout(() => {
      if (!brand.clientOnly) { setPhase("choose"); return; }
      logAction("support");
      if (!live.chat) { setPhase("redirect"); window.location.href = waLink("support", brand.name); return; }
      setOrigem("cliente");
      setPhase("busy");
      botSay("Pra eu te atender, qual é o seu nome?", "ask-name");
    }, at));
    return () => timers.forEach(clearTimeout);
  }, [splash]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, typing, phase, live.msgs]);

  const botSay = (text, next, delay = 1100) => {
    setTimeout(() => setTyping(true), 300);
    setTimeout(() => {
      setTyping(false);
      push({ type: "text", text });
      if (next) setPhase(next);
    }, 300 + delay);
  };

  const pick = (id) => {
    logAction(id);
    if (id === "decline") {
      setPhase("busy");
      push({ from: "me", type: "text", text: "Não quero assinar" });
      botSay(farewell(brand), "declined", 1400);
      return;
    }
    const kind = id === "support" ? "support" : "subscribe";
    if (!live.chat) {
      setPhase("redirect");
      setTimeout(() => { window.location.href = waLink(kind, brand.name); }, 600);
      return;
    }
    setOrigem(kind === "support" ? "cliente" : "assinar");
    setPhase("busy");
    botSay("Pra eu te atender, qual é o seu nome?", "ask-name");
  };

  const startLive = async (phone) => {
    setPhase("starting");
    setTyping(true);
    try {
      const d = await live.chat.start({ name, phone: phoneDigits(phone), origem, funil: brand.id });
      setResumed(!!d?.resumed);
      reportInstalled();
      setTyping(false);
      setPhase("live");
    } catch (e) {
      setTyping(false);
      push({ type: "text", text: e?.message || "Não foi possível iniciar o atendimento. Tente novamente." });
      setPhase("ask-phone");
    }
  };

  const submit = (text) => {
    if (phase === "ask-name") {
      const n = text.slice(0, 60);
      setName(n);
      push({ from: "me", type: "text", text: n });
      setPhase("busy-compose");
      botSay(`Prazer, ${n.split(" ")[0]}! Agora me passa seu WhatsApp com DDD`, "ask-phone");
      return;
    }
    if (phase === "ask-phone") {
      push({ from: "me", type: "text", text });
      if (!isValidPhone(text)) {
        setPhase("busy-compose");
        botSay("Não reconheci esse número. Manda com DDD, por exemplo: 11 91234-5678", "ask-phone", 700);
        return;
      }
      startLive(text);
      return;
    }
    if (phase === "live") live.chat.send(text).catch((e) => toast.error(e?.message || "Não foi possível enviar."));
  };

  const upload = (file) => {
    setUploading(true);
    live.chat.upload(file).catch((e) => toast.error(e?.message || "Não foi possível enviar o arquivo.")).finally(() => setUploading(false));
  };

  const newChat = () => {
    live.reset();
    window.location.reload();
  };

  const composing = COMPOSE.includes(phase) || phase === "busy-compose";
  const visitorSent = live.msgs.some((m) => m.from === "visitor");
  const offlineNote = live.cfg && !live.cfg.online && !live.cfg.ai ? live.cfg.offline_text || "" : "";
  const firstVisitor = firstVisitorIndex(live.msgs);

  return (
    <div className="lz-app">
      <AnimatePresence>{splash && <Splash key="splash" onSkip={() => setSplash(false)} />}</AnimatePresence>
      <ChatHeader typing={typing || !!live.status?.typing} onNewChat={newChat} onInstall={inst.installed ? undefined : inst.install}
        cfg={live.cfg}
        installBar={brand.clientOnly && !inst.installed ? inst.install : undefined}>
        <OpenChromeBar className="mx-3 mb-2" />
      </ChatHeader>
      <main className="lz-chat" data-testid="chat-thread">
        <div className="lz-day">Hoje</div>
        <div className="lz-e2e" data-testid="chat-security-note">
          <Lock size={12} /> As mensagens são protegidas. {brand.official || `Atendimento oficial ${brand.name}.`}
        </div>
        {msgs.filter((m) => m.type !== "install" || !inst.installed).map((m) => (
          <Bubble key={m.id} from={m.from} time={m.time} bare={m.type === "video"} testId={`chat-message-${m.id}`}>
            <Content m={m} onInstall={inst.install} />
          </Bubble>
        ))}
        {resumed && <div className="lz-day lz-resumed" data-testid="chat-resumed-note">Continuando seu atendimento anterior</div>}
        {live.msgs.map((m, i) => (
          <Fragment key={`live-${m.id}`}>
            <Bubble from={m.from === "visitor" ? "me" : "bot"} time={msgTime(m.at)} testId={`live-message-${m.id}`}>
              <LiveContent m={m} chat={live.chat} />
            </Bubble>
            {i === firstVisitor && offlineNote && (
              <Bubble time={msgTime(m.at)} testId="chat-offline-note"><p className="lz-text">{offlineNote}</p></Bubble>
            )}
            {i === 0 && !inst.installed && !brand.clientOnly && (
              <Bubble time={msgTime(m.at)} testId="chat-install-bubble">
                <p className="lz-text">Quer falar com a gente mais rápido? Instale o app de suporte e receba avisos quando responderem.</p>
                <button data-testid="chat-install-button" className="lz-install-btn" onClick={inst.install}>
                  <Download size={17} /> Instalar app
                </button>
              </Bubble>
            )}
          </Fragment>
        ))}
        <AnimatePresence>{typing && <Typing key="typing" />}</AnimatePresence>
        {phase === "live" && visitorSent && <PushBanner chat={live.chat} cfg={live.cfg} />}
        {phase === "redirect" && (
          <p className="lz-hint" data-testid="redirect-hint">Abrindo o WhatsApp...</p>
        )}
        {phase === "declined" && (
          <motion.button data-testid="reconsider-button" className="lz-reconsider" whileTap={{ scale: 0.96 }}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={() => pick("reconsider")}>
            <RotateCcw size={15} /> Mudei de ideia, quero assinar por R$ 25
          </motion.button>
        )}
        {!brand.clientOnly && ["script", "choose", "declined"].includes(phase) && (
          <p className="lz-footer" data-testid="chat-footer">Já é cliente? <a href={brand.support} data-testid="chat-footer-support-link">Suporte</a></p>
        )}
        <div ref={endRef} className={phase === "choose" ? "h-[300px]" : composing ? "h-24" : "h-6"} />
      </main>
      <InstallHelp open={inst.help} onClose={inst.closeHelp} />
      <AnimatePresence>{phase === "choose" && <Choices key="choices" onPick={pick} />}</AnimatePresence>
      {composing && (
        <Composer onSend={submit} busy={phase === "busy-compose" || phase === "starting"}
          tel={phase === "ask-phone"} uploading={uploading}
          placeholder={phase === "ask-name" ? "Digite seu nome" : phase === "ask-phone" ? "(11) 91234-5678" : "Mensagem"}
          onFile={phase === "live" ? upload : undefined} onTyping={phase === "live" ? () => live.chat.typing() : undefined} />
      )}
    </div>
  );
}
